import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import adminApiService from '../../services/adminApiService';

const AdminAgentTickets = () => {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [supportAgents, setSupportAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [assigningTicket, setAssigningTicket] = useState(null);

  useEffect(() => {
    loadAgentTickets();
  }, []);

  useEffect(() => {
    // Filter tickets based on search term
    if (searchTerm.trim() === '') {
      setFilteredTickets(tickets);
    } else {
      const filtered = tickets.filter(ticket => 
        ticket.ticketId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.agentId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.agentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.agentQuery?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.ticketStatus?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTickets(filtered);
    }
  }, [searchTerm, tickets]);

  const loadAgentTickets = async () => {
    try {
      setLoading(true);
      console.log('🎫 Loading agent tickets data...');
      const response = await adminApiService.getAgentTickets();
      console.log('📊 Agent Tickets API Response:', response);
      
      if (response.success) {
        const data = response.data;
        setTickets(data.tickets || []);
        setSupportAgents(data.supportList || []);
        setFilteredTickets(data.tickets || []);
        console.log('✅ Agent tickets data loaded successfully:', data.tickets?.length, 'tickets');
      } else {
        console.error('❌ Agent Tickets API returned error:', response.message);
        toast.error(response.message || 'Failed to load agent tickets');
      }
    } catch (error) {
      console.error('❌ Agent tickets load error:', error);
      toast.error('Failed to load agent tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTicket = async (ticketId) => {
    // Find the dropdown for this specific ticket
    const dropdown = document.querySelector(`select[data-ticket-id="${ticketId}"]`);
    const assignedTo = dropdown ? dropdown.value : null;

    console.log('🎯 Assign button clicked for ticket:', ticketId);
    console.log('🎯 Dropdown found:', dropdown);
    console.log('🎯 Selected support ID:', assignedTo);

    if (!assignedTo) {
      toast.error('Please select a support agent before assigning');
      return;
    }

    try {
      setAssigningTicket(ticketId);
      console.log('🎯 Assigning ticket:', ticketId, 'to support:', assignedTo);
      
      const response = await adminApiService.assignTicket(ticketId, assignedTo);
      
      if (response.success) {
        toast.success('Support assigned successfully');
        await loadAgentTickets(); // Reload tickets to get updated data
      } else {
        toast.error(response.message || 'Failed to assign ticket');
      }
    } catch (error) {
      console.error('❌ Error assigning ticket:', error);
      toast.error('Failed to assign ticket');
    } finally {
      setAssigningTicket(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'OPEN': 'bg-blue-100 text-blue-800',
      'IN_PROGRESS': 'bg-yellow-100 text-yellow-800',
      'RESOLVED': 'bg-green-100 text-green-800',
      'CLOSED': 'bg-gray-100 text-gray-800',
      'PENDING': 'bg-orange-100 text-orange-800'
    };

    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {status || 'UNKNOWN'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Agent Tickets</h1>
          <p className="text-gray-600">Manage and assign agent support tickets</p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Agent Tickets Details</h3>
              <div className="text-sm text-gray-500">
                Total: {filteredTickets.length} tickets
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="max-w-md">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  placeholder="Search by ticket ID, agent ID, name, or query..."
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ticket ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent Query
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTickets.length > 0 ? (
                  filteredTickets.map((ticket, index) => (
                    <tr key={ticket.ticketId || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {ticket.ticketId || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {ticket.agentId || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {ticket.agentName || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                        <div className="truncate" title={ticket.agentQuery}>
                          {ticket.agentQuery || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(ticket.ticketStatus)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center space-x-2">
                          <select
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                            data-ticket-id={ticket.ticketId}
                            defaultValue={ticket.assignedTo || ''}
                            disabled={assigningTicket === ticket.ticketId}
                          >
                            <option value="">Select Support</option>
                            {supportAgents.map((support) => (
                              <option 
                                key={support.supportId} 
                                value={support.supportId}
                                selected={support.supportId === ticket.assignedTo}
                              >
                                {support.supportName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleAssignTicket(ticket.ticketId)}
                          disabled={assigningTicket === ticket.ticketId}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {assigningTicket === ticket.ticketId ? (
                            <div className="flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Assigning...
                            </div>
                          ) : (
                            'Assign'
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      {searchTerm ? 'No tickets found matching your search.' : 'No agent tickets found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {filteredTickets.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-700">
                Showing {filteredTickets.length} of {tickets.length} tickets
                {searchTerm && (
                  <span className="ml-2">
                    (filtered by "{searchTerm}")
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAgentTickets;